import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  DollarSign, Download, Calendar, Loader2, FileText, 
  TrendingUp, Banknote, UserCheck, AlertCircle, Clock, Calculator, Eye
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StaffMySalaryPage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [salarySlips, setSalarySlips] = useState([]);
  const [currentSalary, setCurrentSalary] = useState(null);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);

  useEffect(() => {
    fetchSalaryData();
  }, []);

  const fetchSalaryData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/hr/salary/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalarySlips(response.data?.slips || []);
      setCurrentSalary(response.data?.current || null);
    } catch (error) {
      console.error('Failed to fetch salary data:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadSlip = async (slipId) => {
    try {
      const response = await axios.get(`${API_URL}/api/hr/salary/slip/${slipId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `salary_slip_${slipId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download salary slip');
    }
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    try {
      const date = new Date(monthStr + '-01');
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } catch {
      return monthStr;
    }
  };

  // Slip Detail Component
  const SlipDetailView = ({ slip }) => {
    if (!slip) return null;
    return (
      <div className="space-y-6">
        {/* Calculation Info */}
        {slip.calculation && (
          <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-800 dark:text-blue-200">Salary Calculation</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Monthly Gross</p>
                  <p className="font-semibold">₹{slip.calculation.monthly_gross?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Days in Month</p>
                  <p className="font-semibold">{slip.attendance?.total_days_in_month || 30}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Daily Rate</p>
                  <p className="font-semibold">₹{slip.calculation.daily_salary?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Present Days Salary</p>
                  <p className="font-semibold text-green-600">₹{slip.calculation.present_salary?.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Attendance */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <Calendar className="h-5 w-5 mx-auto text-muted-foreground mb-1" />
              <p className="text-2xl font-bold">{slip.attendance?.working_days || 0}</p>
              <p className="text-xs text-muted-foreground">Working Days</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 dark:bg-green-950">
            <CardContent className="pt-4 text-center">
              <UserCheck className="h-5 w-5 mx-auto text-green-600 mb-1" />
              <p className="text-2xl font-bold text-green-600">{slip.attendance?.present_days || 0}</p>
              <p className="text-xs text-muted-foreground">Present</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 dark:bg-red-950">
            <CardContent className="pt-4 text-center">
              <AlertCircle className="h-5 w-5 mx-auto text-red-600 mb-1" />
              <p className="text-2xl font-bold text-red-600">{slip.attendance?.absent_days || 0}</p>
              <p className="text-xs text-muted-foreground">Absent</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <Clock className="h-5 w-5 mx-auto text-amber-600 mb-1" />
              <p className="text-2xl font-bold text-amber-600">{slip.attendance?.late_days || 0}</p>
              <p className="text-xs text-muted-foreground">Late</p>
            </CardContent>
          </Card>
        </div>

        {/* Earnings & Deductions */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-green-600">
                <TrendingUp className="h-4 w-4" /> Earnings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between py-1.5 border-b">
                <span className="text-muted-foreground">Daily Rate</span>
                <span>₹{(slip.earnings?.daily_rate || slip.calculation?.daily_salary || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b">
                <span className="text-muted-foreground">Present Days ({slip.attendance?.present_days})</span>
                <span>₹{(slip.earnings?.present_days_salary || slip.calculation?.present_salary || 0).toLocaleString()}</span>
              </div>
              {slip.earnings?.overtime_payment > 0 && (
                <div className="flex justify-between py-1.5 border-b">
                  <span className="text-muted-foreground">Overtime</span>
                  <span>₹{slip.earnings.overtime_payment.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between py-2 font-bold text-green-600 text-lg">
                <span>Total Earnings</span>
                <span>₹{(slip.earnings?.gross_earnings || 0).toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-red-600">
                <Banknote className="h-4 w-4" /> Deductions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {slip.deductions?.pf > 0 && (
                <div className="flex justify-between py-1.5 border-b">
                  <span className="text-muted-foreground">Provident Fund</span>
                  <span className="text-red-600">₹{slip.deductions.pf.toLocaleString()}</span>
                </div>
              )}
              {slip.deductions?.professional_tax > 0 && (
                <div className="flex justify-between py-1.5 border-b">
                  <span className="text-muted-foreground">Professional Tax</span>
                  <span className="text-red-600">₹{slip.deductions.professional_tax.toLocaleString()}</span>
                </div>
              )}
              {slip.deductions?.tds > 0 && (
                <div className="flex justify-between py-1.5 border-b">
                  <span className="text-muted-foreground">TDS</span>
                  <span className="text-red-600">₹{slip.deductions.tds.toLocaleString()}</span>
                </div>
              )}
              {slip.deductions?.late_deduction > 0 && (
                <div className="flex justify-between py-1.5 border-b">
                  <span className="text-muted-foreground">Late Penalty</span>
                  <span className="text-red-600">₹{slip.deductions.late_deduction.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between py-2 font-bold text-red-600 text-lg">
                <span>Total Deductions</span>
                <span>₹{(slip.deductions?.total_deductions || 0).toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Net Payable */}
        <Card className="bg-gradient-to-r from-emerald-500 to-green-600 text-white">
          <CardContent className="py-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-emerald-100 text-sm">Net Payable</p>
                <p className="text-3xl font-bold">₹{(slip.net_payable || 0).toLocaleString()}</p>
              </div>
              <Badge variant={slip.payment_status === 'paid' ? 'secondary' : 'outline'} className="text-white border-white">
                {slip.payment_status === 'paid' ? '✓ PAID' : 'PENDING'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title="My Salary">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="My Salary">
      <div className="space-y-6">
        {/* Current Salary Summary */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white overflow-hidden">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100">Current Monthly Salary</p>
                  <p className="text-4xl font-bold mt-2">
                    ₹{(currentSalary?.gross_salary || currentSalary?.net_salary || currentSalary?.basic_salary || 0).toLocaleString()}
                  </p>
                  {currentSalary && (
                    <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-green-200">Basic</p>
                        <p className="font-semibold">₹{(currentSalary.basic_salary || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-green-200">Allowances</p>
                        <p className="font-semibold">+₹{(currentSalary.total_allowances || currentSalary.allowances || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-green-200">Deductions</p>
                        <p className="font-semibold">-₹{(currentSalary.total_deductions || currentSalary.deductions || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                </div>
                <DollarSign className="h-20 w-20 text-green-200 opacity-30" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Salary Slips */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Salary Slips
            </CardTitle>
            <CardDescription>View and download your monthly salary slips</CardDescription>
          </CardHeader>
          <CardContent>
            {salarySlips.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No salary slips available yet</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Salary slips will appear here once processed by admin
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {salarySlips.map((slip, idx) => (
                  <motion.div 
                    key={slip.id || idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Card className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          {/* Month Info */}
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                              <Calendar className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <p className="font-semibold text-lg">{slip.month_name || formatMonth(slip.month)}</p>
                              <p className="text-xs text-muted-foreground font-mono">{slip.slip_number}</p>
                            </div>
                          </div>

                          {/* Key Metrics */}
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-xs text-muted-foreground">Attendance</p>
                              <p className="font-medium">{slip.attendance?.present_days || '-'}/{slip.attendance?.working_days || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Earnings</p>
                              <p className="font-medium text-green-600">₹{(slip.earnings?.gross_earnings || slip.gross_salary || 0).toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Net Pay</p>
                              <p className="font-bold text-emerald-600">₹{(slip.net_payable || slip.net_salary || 0).toLocaleString()}</p>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <Badge variant={slip.payment_status === 'paid' ? 'default' : 'secondary'}>
                              {slip.payment_status === 'paid' ? '✓ Paid' : 'Pending'}
                            </Badge>
                            <Button variant="ghost" size="icon" onClick={() => { setSelectedSlip(slip); setDetailDialog(true); }}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => downloadSlip(slip.id)}>
                              <Download className="h-4 w-4 mr-1" /> PDF
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Slip Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Salary Slip - {selectedSlip?.month_name || formatMonth(selectedSlip?.month)}</DialogTitle>
          </DialogHeader>
          <SlipDetailView slip={selectedSlip} />
          <DialogFooter>
            <Button variant="outline" onClick={() => downloadSlip(selectedSlip?.id)}>
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default StaffMySalaryPage;
